class FaqSearch extends HTMLElement {
  connectedCallback() {
    this.trigger    = this.querySelector('[data-faq-search-open]');
    this.dialog     = this.querySelector('[data-faq-search-dialog]');
    this.input      = this.querySelector('[data-faq-search-input]');
    this.results    = this.querySelector('[data-faq-search-results]');
    this.emptyHint  = this.querySelector('[data-faq-search-empty]');
    this.noResults  = this.querySelector('[data-faq-search-noresults]');
    this.noResultsQ = this.querySelector('[data-faq-search-noresults-q]');
    this.closeBtn   = this.querySelector('[data-faq-search-close]');

    this.index = [];
    this.indexed = false;

    this.trigger.addEventListener('click', () => this.open());
    this.closeBtn.addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.input.addEventListener('input', () => this.filter());
    this.results.addEventListener('click', (e) => {
      const row = e.target.closest('[data-faq-row]');
      if (row) this.toggleRow(row);
    });

    document.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (this.dialog.open) this.close(); else this.open();
      }
    });

    this.dialog.addEventListener('keydown', (e) => this.handleKeyNav(e));
  }

  buildIndex() {
    const accordions = [...document.querySelectorAll('.faq__content .accordion')];
    this.index = accordions.map((a) => {
      const title = a.querySelector('.accordion__toggle')?.textContent.trim() ?? '';
      const answerEl = a.querySelector('.accordion__content');
      const answerHTML = answerEl?.innerHTML ?? '';
      const answerText = answerEl?.textContent.trim().toLowerCase() ?? '';

      let category = '';
      let prev = a.previousElementSibling;
      while (prev) {
        if (prev.classList.contains('faq__category')) {
          category = prev.textContent.trim();
          break;
        }
        prev = prev.previousElementSibling;
      }

      return { title, titleLower: title.toLowerCase(), answerHTML, answerText, category };
    });
    this.indexed = true;
  }

  open() {
    if (!this.indexed) this.buildIndex();
    this.dialog.showModal();
    requestAnimationFrame(() => this.input.focus());
    this.render(this.input.value.trim());
  }

  close() {
    this.dialog.close();
  }

  filter() {
    this.render(this.input.value.trim());
  }

  render(query) {
    const q = query.toLowerCase();
    if (!q) {
      this.results.innerHTML = '';
      this.emptyHint.hidden = false;
      this.noResults.hidden = true;
      return;
    }
    this.emptyHint.hidden = true;

    const matches = this.index.filter((item) => item.titleLower.includes(q));

    if (matches.length === 0) {
      this.results.innerHTML = '';
      this.noResultsQ.textContent = query;
      this.noResults.hidden = false;
      return;
    }
    this.noResults.hidden = true;

    const groups = new Map();
    matches.forEach((m) => {
      const key = m.category || 'Other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    });

    let html = '';
    let idx = 0;
    for (const [category, items] of groups) {
      html += `<div class="k-faq-search__group">`;
      html += `<div class="k-faq-search__category">${escapeHTML(category)}</div>`;
      items.forEach((item) => {
        const rowId = `k-faq-row-${idx}`;
        html += `
          <button type="button" class="k-faq-search__row" role="option" id="${rowId}" data-faq-row aria-expanded="false">
            <span class="k-faq-search__row-text">${highlight(item.title, q)}</span>
            <svg class="k-faq-search__row-chevron" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
              <path d="m1 3 4 4 4-4" fill="none" stroke="currentColor" stroke-linecap="square"/>
            </svg>
          </button>
          <div class="k-faq-search__answer" hidden data-faq-answer-for="${rowId}">${item.answerHTML}</div>
        `;
        idx++;
      });
      html += `</div>`;
    }
    this.results.innerHTML = html;
  }

  toggleRow(row) {
    const expanded = row.getAttribute('aria-expanded') === 'true';
    this.results.querySelectorAll('.k-faq-search__row[aria-expanded="true"]').forEach((r) => {
      if (r !== row) {
        r.setAttribute('aria-expanded', 'false');
        const a = this.results.querySelector(`[data-faq-answer-for="${r.id}"]`);
        if (a) a.hidden = true;
      }
    });
    row.setAttribute('aria-expanded', String(!expanded));
    const answer = this.results.querySelector(`[data-faq-answer-for="${row.id}"]`);
    if (answer) answer.hidden = expanded;
  }

  handleKeyNav(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    const rows = [...this.results.querySelectorAll('.k-faq-search__row')];
    if (rows.length === 0) return;
    const current = document.activeElement?.closest('.k-faq-search__row');
    let nextIdx = rows.indexOf(current);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      nextIdx = (nextIdx + 1) % rows.length;
      rows[nextIdx].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nextIdx = (nextIdx - 1 + rows.length) % rows.length;
      rows[nextIdx].focus();
    } else if (e.key === 'Enter' && current) {
      e.preventDefault();
      this.toggleRow(current);
    }
  }
}

function escapeHTML(s) {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function highlight(text, query) {
  if (!query) return escapeHTML(text);
  const safe = escapeHTML(text);
  const safeQ = escapeHTML(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return safe.replace(new RegExp(safeQ, 'gi'), (m) => `<mark>${m}</mark>`);
}

customElements.define('faq-search', FaqSearch);
